import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import PowerDialerAdminToggle from "@/components/PowerDialerAdminToggle";

const FLAG_LABELS = {
  signups_enabled: "Public signups enabled",
  maintenance_mode: "Maintenance mode (full app down)",
  deal_alerts_polling_enabled: "Deal-alerts background poller",
  email_digest_enabled: "Daily email digest",
  discover_search_enabled: "Discover (RentCast) search",
};

const FLAG_HELP = {
  signups_enabled: "Disabling stops `/api/auth/register` and hides signup CTAs (frontend respects on next page load).",
  maintenance_mode: "Reserved — when on, the app banner notifies users of downtime.",
  deal_alerts_polling_enabled: "Toggles the 30-min RentCast saved-search background sweep.",
  email_digest_enabled: "Toggles the daily Resend digest scheduler.",
  discover_search_enabled: "Hides the Discover map tab for non-admins when off.",
};

export default function AdminFlags() {
  const [flags, setFlags] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/feature-flags");
      setFlags(r.data.flags);
      setDefaults(r.data.defaults);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load flags");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await api.patch("/admin/feature-flags", { flags: next });
      setFlags(r.data.flags);
      toast.success("Flags updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !flags) {
    return (
      <div className="card p-8 text-center text-[var(--ink-2)]">
        <Loader2 size={20} className="animate-spin inline" /> Loading flags…
      </div>
    );
  }

  return (
    <div data-testid="admin-flags">
      {/* Power Dialer platform toggle — its own kill-switch card sitting
          ABOVE the generic feature-flag list because it carries the
          highest legal-exposure of any toggle in the app. */}
      <div className="mb-5">
        <PowerDialerAdminToggle />
      </div>

      <div className="flex justify-end mb-3">
        <button
          type="button"
          className="btn btn-ghost"
          disabled={saving}
          onClick={() => save(defaults)}
          data-testid="admin-flags-reset"
        >
          <RotateCcw size={14} /> Reset to defaults
        </button>
      </div>
      <div className="card divide-y">
        {Object.entries(flags).map(([key, value]) => (
          <div key={key} className="flex items-start justify-between gap-4 p-4" data-testid={`flag-row-${key}`}>
            <div className="min-w-0">
              <div className="font-semibold">{FLAG_LABELS[key] || key}</div>
              <div className="text-xs text-[var(--ink-2)] mt-1">{FLAG_HELP[key]}</div>
            </div>
            <Toggle
              checked={value}
              disabled={saving}
              onChange={(checked) => save({ ...flags, [key]: checked })}
              testid={`flag-${key}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={
        "relative w-11 h-6 rounded-full transition-colors flex-shrink-0 " +
        (checked ? "bg-emerald-500" : "bg-slate-300") +
        (disabled ? " opacity-50 cursor-not-allowed" : " cursor-pointer")
      }
    >
      <span
        className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform"
        style={{ transform: checked ? "translateX(20px)" : "translateX(0)" }}
      />
    </button>
  );
}
